import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Container } from '../Layout/ResponsiveGrid';
import { BookOpen, Star, Timer, AlertTriangle, Code, Package, Globe } from 'lucide-react';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateX(30px); }
  to { opacity: 1; transform: translateX(0); }
`;

const progressAnimation = keyframes`
  from { width: 0; }
  to { width: var(--progress-width); }
`;

// Styled components
const LearningSection = styled.section`
  padding: 6rem 0;
  position: relative;
  background: ${({ theme }) => `${theme.colors.secondary}10`};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 4rem 0;
  }
`;

const SectionHeading = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 80px;
    height: 4px;
    background: ${({ theme }) => theme.colors.accent};
    border-radius: 2px;
  }
`;

const SectionSubheading = styled.p`
  font-size: 1.125rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 3rem;
  max-width: 600px;
`;

const LearningGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
`;

const LearningCard = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.secondary};
  transition: all 0.3s ease;
  height: 100%;
  box-shadow: ${({ theme }) => theme.shadows.md};
  animation: ${fadeIn} 0.5s ease-out forwards;
  animation-delay: ${({ $index }) => `${0.1 * $index}s`};
  opacity: 0;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
    border-color: ${({ theme }) => theme.colors.accent};
    
    .card-icon {
      transform: scale(1.1);
    }
    
    .progress-bar .fill {
      background: ${({ theme }) => theme.colors.accent};
    }
  }
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.accent};
  transition: transform 0.3s ease;
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const CardTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.25rem;
`;

const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ProgressText = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  border-radius: 4px;
  overflow: hidden;

  .fill {
    height: 100%;
    background: ${({ theme }) => `${theme.colors.accent}99`};
    border-radius: 4px;
    width: var(--progress-width);
    transition: all 0.5s ease;
    animation: ${progressAnimation} 1.5s ease-out;
  }
`;

const CardContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Description = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  height: 4.8rem; /* Standardize height to 3 lines */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const TimelineContainer = styled.div`
  margin-top: auto;
`;

const TimelineItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
`;

const TimelineIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${({ theme }) => `${theme.colors.accent}15`};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.accent};
`;

const TimelineText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ResourcesContainer = styled.div`
  margin-top: 1.5rem;
  animation: ${slideIn} 0.5s ease-out forwards;
  animation-delay: 0.2s;
  opacity: 0;
`;

const ResourcesTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const ResourcesList = styled.ul`
  list-style-type: none;
  padding-left: 0.5rem;
  margin: 0;
  height: 5.4rem; /* Standardize height to show 3 items */
  overflow: hidden;
`;

const ResourceItem = styled.li`
  position: relative;
  padding-left: 1.25rem;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 1.5rem; /* Standardize height of each item */
  
  &:before {
    content: '•';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colors.accent};
    font-size: 1.25rem;
    line-height: 1;
  }
`;

const ChallengesContainer = styled(ResourcesContainer)`
  animation-delay: 0.3s;
`;

const learningItems = [
  {
    id: 1,
    title: "C++ Programming",
    icon: Code,
    description: "In-depth knowledge of C++ with a focus on modern features and clean code practices.",
    progress: 90,
    resources: [
      "Training and education",
      "Modern C++ Design Patterns",
      "Clean C++ Project Guidelines",
      "Artificial Intelligence"
    ],
    challenges: [
      "Complex template metaprogramming concepts",
      "Memory management in multi-threaded applications",
      "Understanding advanced optimization techniques"
    ],
    startDate: "2022-09-01",
    estimatedCompletion: "2025-06-30"
  },
  {
    id: 2,
    title: "Python GUI Development",
    icon: Package,
    description: "Learning GUI development with Python using multiple frameworks including PyQt.",
    progress: 60,
    resources: [
      "Vocational School",
      "Self-paced Learning",
      "Internet Research and Documentation",
      "Artificial Intelligence"
    ],
    challenges: [
      "Cross-platform compatibility issues",
      "Event handling in complex UI hierarchies",
      "Connecting PyQt with backend services"
    ],
    startDate: "2024-10-22",
    estimatedCompletion: "2025-04-15"
  },
  {
    id: 3,
    title: "Web Development",
    icon: Globe,
    description: "Deepening knowledge in modern web development with focus on React.js, responsive design, and state management.",
    progress: 10,
    resources: [
      "React.js Documentation",
      "School for further education",
      "Udemy - Complete Web Developer Course",
      "Artificial Intelligence"
    ],
    challenges: [
      "Responsive Design",
      "Understanding React's component lifecycle",
      "HOW TO CENTER A DIV"
    ],
    startDate: "2025-02-02",
    estimatedCompletion: "2025-08-30"
  }
];

const RedesignedLearning = () => {
  return (
    <LearningSection id="learning">
      <Container>
        <SectionHeading>Currently Learning</SectionHeading>
        <SectionSubheading>Always improving and expanding my skills</SectionSubheading>
        
        <LearningGrid>
          {learningItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <LearningCard key={item.id} $index={index}>
                <CardHeader>
                  <IconWrapper className="card-icon">
                    <Icon size={24} />
                  </IconWrapper>
                  <HeaderContent>
                    <CardTitle>{item.title}</CardTitle>
                    <ProgressWrapper>
                      <ProgressText>{item.progress}%</ProgressText>
                      <ProgressBar className="progress-bar">
                        <div 
                          className="fill" 
                          style={{ '--progress-width': `${item.progress}%` }} 
                        />
                      </ProgressBar>
                    </ProgressWrapper>
                  </HeaderContent>
                </CardHeader>
                
                <CardContent>
                  <Description>{item.description}</Description>
                  
                  <TimelineContainer>
                    <TimelineItem>
                      <TimelineIcon>
                        <Timer size={16} />
                      </TimelineIcon>
                      <TimelineText>
                        Started: {new Date(item.startDate).toLocaleDateString()}
                      </TimelineText>
                    </TimelineItem>
                    
                    <TimelineItem>
                      <TimelineIcon>
                        <Timer size={16} />
                      </TimelineIcon>
                      <TimelineText>
                        Expected completion: {new Date(item.estimatedCompletion).toLocaleDateString()}
                      </TimelineText>
                    </TimelineItem>
                  </TimelineContainer>
                  
                  <ResourcesContainer>
                    <ResourcesTitle>
                      <BookOpen size={18} />
                      Learning Resources
                    </ResourcesTitle>
                    <ResourcesList>
                      {item.resources.map((resource, i) => (
                        <ResourceItem key={i}>{resource}</ResourceItem>
                      ))}
                    </ResourcesList>
                  </ResourcesContainer>
                  
                  <ChallengesContainer>
                    <ResourcesTitle>
                      <AlertTriangle size={18} />
                      Current Challenges
                    </ResourcesTitle>
                    <ResourcesList>
                      {item.challenges.map((challenge, i) => (
                        <ResourceItem key={i}>{challenge}</ResourceItem>
                      ))}
                    </ResourcesList>
                  </ChallengesContainer>
                </CardContent>
              </LearningCard>
            );
          })}
        </LearningGrid>
      </Container>
    </LearningSection>
  );
};

export default RedesignedLearning;